import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { SetActiveItem } from '../../../redux/activeItem/actions';
import ActiveHelper from '../../../components/common/activeHelper';
import { accessTypes, deviceWidth } from '../../../helpers/Utils';
import { Colxx } from '../../../components/common/CustomBootstrap';
import {
  DISCOUNT_TYPES,
  DISCOUNT_TYPE_KEYS,
  getPromoTableDisplay,
  normalizeLegacyOffer,
} from '../../../helpers/promocodeTypes';

/** Price hint for up_selling: Product uses numeric `price`; Service uses a map of modifiers with `.price`. */
function formatUpsellingPriceSuffix(price) {
  if (price == null || price === '') return '';
  if (typeof price === 'number' && Number.isFinite(price)) return `${price} AED`;
  if (typeof price === 'string' && price !== '' && Number.isFinite(Number(price))) {
    return `${Number(price)} AED`;
  }
  if (typeof price === 'object' && !Array.isArray(price)) {
    const nums = Object.values(price)
      .map((m) => {
        if (m != null && typeof m === 'object' && m.price != null && m.price !== '') {
          return Number(m.price);
        }
        return Number(m);
      })
      .filter((v) => Number.isFinite(v));
    if (!nums.length) return '';
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    if (min === max) return `${min} AED`;
    return `${min}–${max} AED`;
  }
  return '';
}

function upsellingNameWithPrice(rec) {
  if (!rec) return '';
  const base = rec.name?.en || rec.name || '';
  const sfx = formatUpsellingPriceSuffix(rec.price);
  return sfx ? `${base} (${sfx})` : base;
}

const Promocodes = () => {
  const dispatch = useDispatch();
  const { activeItem } = useSelector(state => state.activeItem);
  const { user } = useSelector(state => state.authUser);
  const search = useSelector(state => state.settings.search) || '';
  const { promocodes, services, advServices, provider, up_selling } = useSelector(state => state.providers);
  const [filters, setFilters] = useState({});
  const [filteredPromocodes, setFilteredPromocodes] = useState([]);

  // Local filtering logic + normalize legacy (flat percent/cap) to nested shape
  useEffect(() => {
    let filtered = Object.values(promocodes || {});

    if (filters?.sp_id?.key) {
      filtered = filtered.filter(promo => promo.sp_id === filters.sp_id.key);
    }

    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      filtered = filtered.filter(promo => {
        const codeMatch = promo?.code?.toLowerCase().includes(searchLower);
        const providerMatch = provider?.[promo?.sp_id]?.name?.toLowerCase().includes(searchLower);
        return codeMatch || providerMatch;
      });
    }

    setFilteredPromocodes(filtered.map(p => normalizeLegacyOffer(p)));
  }, [filters, promocodes, search, provider]);

  const discountTypeOptions = useMemo(
    () => DISCOUNT_TYPE_KEYS.map(k => ({ key: k, label: DISCOUNT_TYPES[k].label, id: k })),
    []
  );

  const addonOptions = useMemo(() => {
    let list = Object.values(up_selling || {}).filter(r => r.type === 'Service');
    const spId = activeItem?.general?.sp_id;
    const discountType = activeItem?.general?.discountType;
    if (discountType === 'free_addon' && spId) {
      list = list.filter(r => r.sp_id === spId);
    }
    return list.map(rec => ({
      ...rec,
      name: upsellingNameWithPrice(rec),
      key: rec.key,
      id: rec.key,
    }));
  }, [up_selling, activeItem?.general?.sp_id, activeItem?.general?.discountType]);

  const elements = useMemo(() => {
    const allServices = services && advServices && Object.values(services).concat(Object.values(advServices));
    const discountType = activeItem?.general?.discountType || 'percentage';

    const hasWashingService = activeItem?.general?.services?.some(service => {
      const foundService = allServices?.find(s => s.key === service.key || s.key === service);
      return foundService?.washingService === true || foundService?.type === 'washingService';
    });

    const showPercentageFields = discountType === 'percentage';
    const showFixedAmountField = discountType === 'fixed_amount';
    const showFirstOrder = discountType === 'first_order';
    const showFreeAddon = discountType === 'free_addon';
    const showCashback = discountType === 'cashback';
    const showBogo = discountType === 'bogo';

    return [{
      key: 'generalInfo',
      data: [
        {
          key: 'global',
          title: 'Auto Applied',
          width: '12',
          type: 'switch',
          hideTitle: true,
          hide: !activeItem?.general?.sp_id || activeItem?.general?.individual === true,
          description: '* Enabling this will auto-apply this offer for the selected services. Each service can have only one auto-applied offer.',
        },
        {
          key: 'chill',
          title: 'Chill Related',
          width: '12',
          type: 'switch',
          hideTitle: true,
          description: '* When enabled, the discount cost will be charged to Chill instead of the service provider',
        },
        {
          key: 'individual',
          title: 'Individual Promo',
          width: '12',
          type: 'switch',
          hideTitle: true,
          hide: activeItem?.general?.global === true,
          description: '* When enabled, this promo is reserved for specific individuals and will not be listed in the mobile app checkout screen (still redeemable by code).',
        },
        { key: 'code', title: 'Promo Code', width: '12', type: 'text', required: true },
        {
          key: 'sp_id',
          title: 'Service Provider (optional)',
          width: '12',
          data: Object.values(provider || {}).map(rec => ({ ...rec, name: rec.name, key: rec.key, id: rec.key })),
          type: 'select',
          isClearable: true,
        },
      
        {
          key: 'services',
          title: 'Services (optional)',
          width: '12',
          data: allServices || [],
          type: 'multi-select',
        },
        {
          key: 'washingTypes',
          title: 'Washing Type',
          width: '12',
          type: 'multi-select',
          data: [
            { name: 'Pressure Wash', key: 'pressureWash', id: 'pressureWash' },
            { name: 'Waterless Wash', key: 'waterlessWash', id: 'waterlessWash' },
            { name: 'Steam Wash', key: 'steamWash', id: 'steamWash' },
          ],
          hide: !hasWashingService,
          description: '* Select one or more washing types for washing services',
        },
        { key: 'count', title: 'Number Of Promos', width: '6', type: 'number', required: true },
        { key: 'limited', title: 'Limited Per User', width: '6', type: 'number' },
        { key: 'startDate', title: 'Start Date (optional)', width: '6', type: 'date' },
        { key: 'endDate', title: 'Expiration Date', width: '6', type: 'date', required: true },
        {
          key: 'discountType',
          title: 'Discount Type',
          width: '12',
          type: 'select',
          data: discountTypeOptions,
          required: true,
        },
        // Percentage: percent, cap
        {
          parent: 'percentage',
          key: 'percent',
          title: 'Discount Value (%)',
          width: '12',
          type: 'number',
          required: showPercentageFields,
          hide: !showPercentageFields,
        },
        {
          parent: 'percentage',
          key: 'cap',
          title: 'Maximum Value (AED)',
          width: '12',
          type: 'number',
          required: showPercentageFields,
          hide: !showPercentageFields,
        },
        // Fixed amount
        {
          parent: 'fixedAmount',
          key: 'amount',
          title: 'Amount (AED)',
          width: '12',
          type: 'number',
          required: showFixedAmountField,
          hide: !showFixedAmountField,
        },
        // First/X order: order number, type (percent/fixed), value, cap (for percent only)
        {
          parent: 'firstOrder',
          key: 'orderNumber',
          title: 'Apply to Order #',
          width: '12',
          type: 'number',
          required: showFirstOrder,
          hide: !showFirstOrder,
          description: 'Applies to orders 1 through X. E.g. 3 = applies to 1st, 2nd, and 3rd order (not 4th onwards).',
        },
        {
          parent: 'firstOrder',
          key: 'type',
          title: 'Discount Type',
          width: '12',
          type: 'select',
          data: [
            { key: 'percent', id: 'percent', name: 'Percent', label: 'Percent' },
            { key: 'fixed', id: 'fixed', name: 'Fixed (AED)', label: 'Fixed (AED)' },
          ],
          hide: !showFirstOrder,
        },
        {
          parent: 'firstOrder',
          key: 'value',
          title: 'Discount Value',
          width: '12',
          type: 'number',
          required: showFirstOrder,
          hide: !showFirstOrder,
        },
        {
          parent: 'firstOrder',
          key: 'cap',
          title: 'Max (AED)',
          width: '12',
          type: 'number',
          hide: !showFirstOrder || activeItem?.general?.firstOrder?.type === 'fixed',
        },
        // Add-on discount: same shape as first order (type, value, cap)
        {
          parent: 'freeAddon',
          key: 'addonId',
          title: 'Add-on',
          width: '12',
          type: 'select',
          data: addonOptions,
          required: showFreeAddon,
          hide: !showFreeAddon,
        },
        {
          parent: 'freeAddon',
          key: 'type',
          title: 'Discount Type',
          width: '12',
          type: 'select',
          data: [
            { key: 'percent', id: 'percent', name: 'Percent', label: 'Percent' },
            { key: 'fixed', id: 'fixed', name: 'Fixed (AED)', label: 'Fixed (AED)' },
          ],
          hide: !showFreeAddon,
        },
        {
          parent: 'freeAddon',
          key: 'value',
          title: 'Discount Value',
          width: '12',
          type: 'number',
          required: showFreeAddon,
          hide: !showFreeAddon,
          description: 'Percent off the add-on, or fixed AED off. Use 100% for a fully free add-on.',
        },
        {
          parent: 'freeAddon',
          key: 'cap',
          title: 'Max (AED)',
          width: '12',
          type: 'number',
          hide: !showFreeAddon || activeItem?.general?.freeAddon?.type === 'fixed',
        },
        // Voucher
        {
          parent: 'cashback',
          key: 'type',
          title: 'Voucher Type',
          width: '12',
          type: 'select',
          data: [
            { key: 'percent', id: 'percent', name: 'Percent', label: 'Percent' },
            { key: 'fixed', id: 'fixed', name: 'Fixed (AED)', label: 'Fixed (AED)' },
          ],
          hide: !showCashback,
        },
        {
          parent: 'cashback',
          key: 'value',
          title: 'Voucher Value',
          width:'12',
          type: 'number',
          required: showCashback,
          hide: !showCashback,
        },
        {
          parent: 'cashback',
          key: 'cap',
          title: 'Voucher Cap (AED)',
          width: '12',
          type: 'number',
          hide: !showCashback || activeItem?.general?.cashback?.type === 'fixed',
        },
        {
          parent: 'cashback',
          key: 'expiryDays',
          title: 'Voucher Credit Valid For (days)',
          width: '12',
          type: 'number',
          hide: !showCashback,
          description: 'Number of days the credited amount is valid in wallet (optional)',
        },
        {
          parent: 'cashback',
          key: 'numberOfSplits',
          title: 'Number of wallet credits',
          width: '12',
          type: 'number',
          hide: !showCashback,
          description:
            'Split the total voucher into this many separate wallet entries (same expiry). Default is 1. Customer app should send price.cashbackNumberOfSplits when booking.',
        },
        // BOGO: percent off next service
        {
          parent: 'bogo',
          key: 'percent',
          title: 'Discount (%) on Next Service',
          width: '12',
          type: 'number',
          required: showBogo,
          hide: !showBogo,
          description: 'e.g. 100 for free service. Applied as % of service price at redemption.',
        },
        {
          parent: 'bogo',
          key: 'cap',
          title: 'Max Discount (AED) – optional',
          width: '12',
          type: 'number',
          hide: !showBogo,
          description: 'Cap the discount amount (optional)',
        },
        {
          parent: 'bogo',
          key: 'expiryDays',
          title: 'BOGO Valid For (days)',
          width: '12',
          type: 'number',
          required: showBogo,
          hide: !showBogo,
          description: 'Number of days the BOGO is valid in wallet',
        },
        {
          parent: 'bogo',
          key: 'serviceId',
          title: 'Specific Service (optional)',
          width: '12',
          type: 'select',
          data: (allServices || []).map(s => ({ key: s.key, id: s.key, name: s.name?.en || s.name, label: s.name?.en || s.name })),
          hide: !showBogo,
          description: 'Limit to specific service. Leave empty for any service.',
        },
        {
          parent: 'bogo',
          key: 'bogoWashingTypes',
          title: 'Washing Type (optional)',
          width: '12',
          type: 'multi-select',
          data: [
            { name: 'Pressure Wash', key: 'pressureWash', id: 'pressureWash' },
            { name: 'Waterless Wash', key: 'waterlessWash', id: 'waterlessWash' },
            { name: 'Steam Wash', key: 'steamWash', id: 'steamWash' },
          ],
          hide: !showBogo || !(activeItem?.general?.bogo?.serviceId && allServices?.find(s => (s.key === activeItem?.general?.bogo?.serviceId) && (s?.washingService === true || s?.type === 'washingService'))),
          description: 'Limit BOGO to specific washing type(s). Only shown when selected service is a washing service.',
        },
       
      ],
    }];
  }, [
    services,
    advServices,
    provider,
    activeItem?.general?.sp_id,
    activeItem?.general?.services,
    activeItem?.general?.discountType,
    activeItem?.general?.firstOrder?.type,
    activeItem?.general?.freeAddon?.type,
    activeItem?.general?.cashback?.type,
    activeItem?.general?.bogo?.serviceId,
    activeItem?.general?.individual,
    activeItem?.general?.global,
    discountTypeOptions,
    addonOptions,
  ]);

  const tableColumns = useMemo(() => {
    return [
      { Header: 'Status', accessor: 'active', width: 120, type: 'switch' },
      { Header: 'Code', accessor: 'code', width: 150, type: 'label' },
      {
        Header: 'Type',
        accessor: 'discountType',
        width: 120,
        type: 'label',
        show: deviceWidth() === 'desktop',
        formatValue: (value) => DISCOUNT_TYPES[value]?.label ?? value ?? '--',
      },
      {
        Header: 'Chill Related',
        accessor: 'chill',
        width: 130,
        type: 'label',
        show: deviceWidth() === 'desktop',
        formatValue: (value) => {
          if (value === true || value === 'true') return 'Yes';
          if (value === false || value === 'false') return 'No';
          return '--';
        },
      },
      {
        Header: 'Washing Type',
        accessor: 'washingTypes',
        width: 130,
        type: 'label',
        show: deviceWidth() === 'desktop',
        formatValue: (value) => {
          if (!value) return '--';
          if (Array.isArray(value)) {
            return value.map(v => {
              if (v.key === 'pressureWash' || v === 'pressureWash') return 'Pressure Wash';
              if (v.key === 'waterlessWash' || v === 'waterlessWash') return 'Waterless Wash';
              if (v.key === 'steamWash' || v === 'steamWash') return 'Steam Wash';
              return v.name || v;
            }).join(', ');
          }
          if (value === 'pressureWash') return 'Pressure Wash';
          if (value === 'waterlessWash') return 'Waterless Wash';
          if (value === 'steamWash') return 'Steam Wash';
          return '--';
        },
      },
      { Header: 'Number of Promos', accessor: 'count', width: 150, type: 'label', show: deviceWidth() === 'desktop' },
      {
        Header: 'Auto Applied',
        accessor: 'global',
        width: 110,
        type: 'label',
        show: deviceWidth() === 'desktop',
        formatValue: (value) => (value === true || value === 'true' ? 'Yes' : 'No'),
      },
      {
        Header: 'Offer',
        accessor: 'discountDisplay',
        width: 180,
        type: 'label',
        show: deviceWidth() === 'desktop',
        formatValue: (_, row) => {
          const addonId = row?.freeAddon?.addonId;
          const addonLabel = addonId
            ? upsellingNameWithPrice(up_selling?.[addonId]) || addonId
            : null;
          const serviceId = row?.bogo?.serviceId;
          const serviceLabel = serviceId
            ? (services?.[serviceId]?.name?.en || advServices?.[serviceId]?.name?.en || services?.[serviceId]?.name || advServices?.[serviceId]?.name || serviceId)
            : null;
          return getPromoTableDisplay(row, { addonLabel, serviceLabel });
        },
      },
      { Header: 'Created', accessor: 'created', width: 130, type: 'date', show: deviceWidth() === 'desktop' },
      { Header: 'Starts', accessor: 'startDate', width: 130, type: 'date', show: deviceWidth() === 'desktop' },
      { Header: 'Expires', accessor: 'endDate', width: 130, type: 'date', show: deviceWidth() === 'desktop' },
      { Header: 'Service Provider', accessor: 'sp_id', type: 'provider', show: deviceWidth() === 'desktop' },
    ];
  }, [up_selling, services, advServices]);

  // Create filter component for service provider selection
  const filterComponent = useMemo(() => {
    if (!accessTypes.includes(user?.accessType)) {
      return null;
    }

    const providerOptions = Object.values(provider || {});

    return (
      <Colxx xxs="12" md="3" className='mb-3' >
        <Select
          isClearable={true}
          placeholder="Filter by Service Providers"
          name="sp_id"
          options={providerOptions}
          className="basic-multi-select"
          classNamePrefix="select"
          value={filters?.sp_id || undefined}
          onChange={val => setFilters(prev => ({ ...prev, sp_id: val || undefined }))}
        />
      </Colxx>
    );
  }, [provider, user?.accessType, filters]);

  return (
    <div>
      <ActiveHelper
        renderAsModal={true}
        modalSize='lg'
        modalBackdrop='static'
        // wrapClassName="modal-right"
        data={filteredPromocodes || []}
        title="Promo Codes"
        selected='offers'
        elements={elements}
        tableColumns={tableColumns}
        permenatDelete={true}
        defaultSorting="active"
        filters={filters}
        filterComponent={filterComponent}
        showCreateButton={true}
        customCreateFunction={() =>
          dispatch(SetActiveItem({ general: { addNew: true, discountType: 'percentage', individual: true } }))
        }
      />
    </div>
  );
};


export default Promocodes;
